import React from 'react';
import { useCamera } from '@src/features/order/hooks';
import { useRecording } from '@src/features/order/hooks/useRecording';
import { ReloadIcon, SwapIcon, SplitIcon, CollapseIcon } from './icons';
import { ArcCircularProgress, ArcIconButton, ArcStack, ArcText } from '@src/core/ui';
import { useDisclosure } from '@src/core/hooks';
import { CameraViewProps, ToolbarAction } from './types';
import CameraToolbar from './camera-toolbar';
import RecordingIndicator from './recording-indicator';
import CameraActions from '@src/features/order/components/camera-actions';
import GoHomeAction from '@src/features/order/components/go-home-action';
import WinchDownAction from '@src/features/order/components/winch-down-action';
import { useCameraLayout } from './camera-layout-context';

const CameraView: React.FC<CameraViewProps> = (props) => {
  const { serialNumber, isViewer, isEnRoute, resetGoHere } = props;

  const { viewMode, setViewMode, registerReconnect, collapsed, toggleCollapse } = useCameraLayout();
  const isOverlay = viewMode === 'default';
  const showToolbar = true;

  const { open: expanded, toggle: toggleExpand } = useDisclosure();

  const { videoRef, isReconnecting, reconnect, isLoading } = useCamera(serialNumber, isViewer ?? false);
  const { isRecording, requestRecording } = useRecording(serialNumber);

  React.useEffect(() => {
    registerReconnect(reconnect, isReconnecting);
  }, [reconnect, isReconnecting, registerReconnect]);

  const loading = isLoading || isReconnecting;

  const toolbarActions: ToolbarAction[] = [
    {
      key: 'Reconnect',
      icon: <ReloadIcon />,
      onClick: reconnect,
      disabled: isReconnecting,
    },
    {
      key: 'Swap video',
      icon: <SwapIcon />,
      onClick: () => setViewMode('pip'),
    },
    {
      key: 'Split View',
      icon: <SplitIcon />,
      onClick: () => setViewMode('split'),
    },
    {
      key: collapsed ? 'Expand' : 'Collapse',
      icon: <CollapseIcon />,
      onClick: toggleCollapse,
    },
  ];

  const isCollapsedOverlay = isOverlay && collapsed;

  return (
    <>
      {isCollapsedOverlay && (
        <ArcIconButton
          onClick={toggleCollapse}
          sx={{
            left: 5,
            bottom: 5,
            zIndex: 100,
            position: 'absolute',
            bgcolor: 'background.default',
            borderRadius: 1,
            p: 0.5,
            '&:hover': { bgcolor: 'background.paper' },
            '& .MuiSvgIcon-root': { fontSize: 20 },
          }}>
          <CollapseIcon sx={{ transform: 'rotate(180deg)' }} />
        </ArcIconButton>
      )}

      <ArcStack 
        p={1}
        zIndex={100}
        bgcolor='background.default'
        display={isCollapsedOverlay ? 'none' : 'flex'}
        {...(isOverlay ? {
          left: 5,
          bottom: 5,
          borderRadius: 1,
          position: 'absolute' as const,
          height: expanded ? 'auto' : 200,
          width: expanded ? 'calc(100% - 10px)' : 400,
        } : {
          position: 'relative' as const,
          width: '100%',
          height: '100%',
          borderRadius: 0,
        })}>

        <RecordingIndicator isRecording={isRecording} onClick={requestRecording} />

        {showToolbar && <CameraToolbar actions={toolbarActions} />}

        {expanded && !isViewer && <CameraActions serialNumber={serialNumber} />}

        {loading && (
          <ArcStack
            spacing={1} 
            width='100%'
            height='100%'
            position='absolute'
            top={0}
            left={0}
            alignItems='center'
            justifyContent='center'>
            <ArcCircularProgress color='ghost' />
            <ArcStack>
              <ArcText.h2 textAlign='center'> Connecting... </ArcText.h2>
              <ArcText.caption textAlign='center'> Please wait </ArcText.caption>
            </ArcStack>
          </ArcStack>
        )}

        <ArcStack 
          width='100%' 
          height='100%' 
          overflow='hidden' 
          alignItems='center' 
          justifyContent='center'>
          <video 
            autoPlay 
            playsInline
            muted
            ref={videoRef}
            style={{
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              objectPosition: 'center'
            }} />
        </ArcStack>

        {(isEnRoute && !isViewer) && (
          <ArcStack 
            p={1}
            left={10}
            right={10} 
            bottom={20}
            spacing={1} 
            direction='row'
            position='absolute' 
            justifyContent='center'>
            <GoHomeAction serialNumber={serialNumber} resetGoHere={resetGoHere} />
            <WinchDownAction serialNumber={serialNumber} />
          </ArcStack>     
        )}
      </ArcStack>
    </>
  );
};

export default CameraView;
